import React, { useRef, useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import html2canvas from 'html2canvas';
import Swal from 'sweetalert2';
import { db } from '../../../firebase';
import { doc, updateDoc } from 'firebase/firestore';

const themes = [
    { name: 'Dark Charcoal', bg: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', textAccent: '#10b981', glow: 'rgba(26, 122, 94, 0.2)' },
    { name: 'Royal Emerald', bg: 'linear-gradient(135deg, #064e3b 0%, #022c22 100%)', textAccent: '#34d399', glow: 'rgba(52, 211, 153, 0.2)' },
    { name: 'Sapphire Blue', bg: 'linear-gradient(135deg, #1e3a8a 0%, #0f172a 100%)', textAccent: '#38bdf8', glow: 'rgba(56, 189, 248, 0.2)' },
    { name: 'Midnight Purple', bg: 'linear-gradient(135deg, #4c1d95 0%, #1e1b4b 100%)', textAccent: '#a78bfa', glow: 'rgba(167, 139, 250, 0.2)' },
    { name: 'Coral Sunset', bg: 'linear-gradient(135deg, #7c2d12 0%, #4c1d95 100%)', textAccent: '#fdba74', glow: 'rgba(253, 186, 116, 0.2)' },
    { name: 'Custom Theme', bg: 'custom', textAccent: 'custom', glow: 'custom' }
];

const taglines = {
    EN: [
        { headline: "Secure Your Financial Future Today.", desc: "Get a comprehensive financial diagnosis and build a roadmap to wealth." },
        { headline: "Smart Money Planning, Secured Family.", desc: "Make your investments work for you with professional wealth strategies." },
        { headline: "Plan Your Retirement with Confidence.", desc: "Take control of your savings today and secure a peaceful, stress-free retirement." }
    ],
    MR: [
        { headline: "तुमचे आर्थिक भविष्य आजच सुरक्षित करा.", desc: "मिळवा सखोल आर्थिक विश्लेषण आणि समृद्धीचा अचूक मार्ग." },
        { headline: "स्मार्ट आर्थिक नियोजन, सुखी कुटुंबाचे आश्वासन.", desc: "गुंतवणूक आणि आर्थिक सुरक्षिततेसाठी आजच विश्वासाने पाऊल टाका." },
        { headline: "निवृत्तीचे (Retirement) उत्तम नियोजन करा.", desc: "तुमच्या सेवानिवृत्तीला द्या आर्थिक स्वावलंबन आणि निश्चिंत सुरक्षेचे बळ." }
    ]
};

// Convert Hex (#RRGGBB) to HSV (h: 0-360, s: 0-100, v: 0-100)
const hexToHsv = (hex) => {
    let r = parseInt(hex.slice(1, 3), 16) / 255;
    let g = parseInt(hex.slice(3, 5), 16) / 255;
    let b = parseInt(hex.slice(5, 7), 16) / 255;

    let max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, v = max;

    let d = max - min;
    s = max === 0 ? 0 : d / max;

    if (max === min) {
        h = 0; // achromatic
    } else {
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h /= 6;
    }
    return { h: Math.round(h * 360), s: Math.round(s * 100), v: Math.round(v * 100) };
};

// Convert HSV to Hex
const hsvToHex = (h, s, v) => {
    s /= 100;
    v /= 100;
    let c = v * s;
    let x = c * (1 - Math.abs((h / 60) % 2 - 1));
    let m = v - c;
    let r = 0, g = 0, b = 0;

    if (h >= 0 && h < 60) { r = c; g = x; b = 0; }
    else if (h >= 60 && h < 120) { r = x; g = c; b = 0; }
    else if (h >= 120 && h < 180) { r = 0; g = c; b = x; }
    else if (h >= 180 && h < 240) { r = 0; g = x; b = c; }
    else if (h >= 240 && h < 300) { r = x; g = 0; b = c; }
    else if (h >= 300 && h <= 360) { r = c; g = 0; b = x; }

    const toHex = (x) => {
        let hex = Math.round((x + m) * 255).toString(16);
        return hex.length === 1 ? '0' + hex : hex;
    };
    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
};

// Embedding Custom Premium Color Picker Widget
const ColorPickerWidget = ({ activeColor, onColorChange, label }) => {
    const svRef = useRef(null);
    const hueRef = useRef(null);
    
    const { h, s, v } = hexToHsv(activeColor);
    
    const [isDraggingSv, setIsDraggingSv] = useState(false);
    const [isDraggingHue, setIsDraggingHue] = useState(false);

    const updateSv = (clientX, clientY) => {
        if (!svRef.current) return;
        const rect = svRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
        const y = Math.max(0, Math.min(rect.height, clientY - rect.top));
        
        const sat = Math.round((x / rect.width) * 100);
        const val = Math.round((1 - y / rect.height) * 100);
        
        onColorChange(hsvToHex(h, sat, val));
    };

    const updateHue = (clientX) => {
        if (!hueRef.current) return;
        const rect = hueRef.current.getBoundingClientRect();
        const x = Math.max(0, Math.min(rect.width, clientX - rect.left));
        
        const hue = Math.round((x / rect.width) * 360);
        onColorChange(hsvToHex(hue, s, v));
    };

    useEffect(() => {
        const handleMouseMove = (e) => {
            if (isDraggingSv) {
                updateSv(e.clientX, e.clientY);
            }
            if (isDraggingHue) {
                updateHue(e.clientX);
            }
        };

        const handleMouseUp = () => {
            setIsDraggingSv(false);
            setIsDraggingHue(false);
        };

        if (isDraggingSv || isDraggingHue) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
            window.addEventListener('touchmove', handleMouseMove);
            window.addEventListener('touchend', handleMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleMouseMove);
            window.removeEventListener('touchend', handleMouseUp);
        };
    }, [isDraggingSv, isDraggingHue, h, s, v]);

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', maxWidth: '320px', background: '#1e293b', padding: '14px', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.15)', color: 'white', border: '1px solid #334155' }}>
            <span style={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</span>
            
            {/* Saturation / Brightness Area Box */}
            <div 
                ref={svRef}
                onMouseDown={(e) => { setIsDraggingSv(true); updateSv(e.clientX, e.clientY); }}
                onTouchStart={(e) => { setIsDraggingSv(true); updateSv(e.touches[0].clientX, e.touches[0].clientY); }}
                style={{
                    height: '140px',
                    borderRadius: '8px',
                    position: 'relative',
                    cursor: 'crosshair',
                    backgroundColor: `hsl(${h}, 100%, 50%)`,
                    overflow: 'hidden',
                    touchAction: 'none'
                }}
            >
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to right, #fff, transparent)' }}></div>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'linear-gradient(to top, #000, transparent)' }}></div>
                
                {/* Marker drag circle */}
                <div style={{
                    width: '14px',
                    height: '14px',
                    borderRadius: '50%',
                    border: '2px solid white',
                    boxShadow: '0 0 4px rgba(0,0,0,0.6)',
                    position: 'absolute',
                    transform: 'translate(-7px, -7px)',
                    left: `${s}%`,
                    top: `${100 - v}%`,
                    pointerEvents: 'none',
                    backgroundColor: activeColor
                }}></div>
            </div>

            {/* Slider track row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '4px' }}>
                {/* Circular preview indicator */}
                <div style={{
                    width: '30px',
                    height: '30px',
                    borderRadius: '50%',
                    backgroundColor: activeColor,
                    border: '2px solid rgba(255,255,255,0.3)',
                    boxShadow: '0 0 5px rgba(0,0,0,0.4)',
                    flexShrink: 0
                }}></div>
                
                {/* Hue slider track */}
                <div 
                    ref={hueRef}
                    onMouseDown={(e) => { setIsDraggingHue(true); updateHue(e.clientX); }}
                    onTouchStart={(e) => { setIsDraggingHue(true); updateHue(e.touches[0].clientX); }}
                    style={{
                        flex: 1,
                        height: '14px',
                        borderRadius: '7px',
                        background: 'linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)',
                        position: 'relative',
                        cursor: 'pointer',
                        touchAction: 'none'
                    }}
                >
                    {/* Hue Thumb circle */}
                    <div style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '50%',
                        border: '2px solid white',
                        boxShadow: '0 0 3px rgba(0,0,0,0.5)',
                        position: 'absolute',
                        top: '50%',
                        transform: 'translate(-9px, -50%)',
                        left: `${(h / 360) * 100}%`,
                        backgroundColor: `hsl(${h}, 100%, 50%)`,
                        pointerEvents: 'none'
                    }}></div>
                </div>
            </div>
            
            {/* Hex Display input code */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', background: '#0f172a', padding: '8px 12px', borderRadius: '6px', marginTop: '2px', border: '1px solid #334155' }}>
                <span style={{ color: '#64748b', fontWeight: 700 }}>HEX CODE:</span>
                <span style={{ fontWeight: 800, letterSpacing: '0.5px', color: '#38bdf8' }}>{activeColor.toUpperCase()}</span>
            </div>
        </div>
    );
};

const MarketingKit = ({ 
    userId, 
    franchiseName, 
    referralCode, 
    referralLink, 
    savedThemeName, 
    dbCustomColor1, 
    dbCustomColor2, 
    dbCustomAccent,
    userEmail,
    userPhone
}) => {
    const posterRef = useRef(null);
    const [selectedTheme, setSelectedTheme] = useState(themes[0]);
    const [customColor1, setCustomColor1] = useState(dbCustomColor1 || '#1e3a8a');
    const [customColor2, setCustomColor2] = useState(dbCustomColor2 || '#0f172a');
    const [customAccent, setCustomAccent] = useState(dbCustomAccent || '#38bdf8');
    const [isSaving, setIsSaving] = useState(false);

    // Active tab state for color picker
    const [activePickerTab, setActivePickerTab] = useState('color1');

    // Dynamic Poster Customization States
    const [language, setLanguage] = useState('MR'); // Default Marathi
    const [taglinePreset, setTaglinePreset] = useState(0);
    const [headlineText, setHeadlineText] = useState('');
    const [descText, setDescText] = useState('');
    
    const [showEmail, setShowEmail] = useState(false);
    const [showPhone, setShowPhone] = useState(false);
    const [templateStyle, setTemplateStyle] = useState('dark'); // 'dark' or 'light'

    useEffect(() => {
        if (savedThemeName) {
            const foundTheme = themes.find(t => t.name === savedThemeName);
            if (foundTheme) {
                setSelectedTheme(foundTheme);
            }
        }
    }, [savedThemeName]);

    useEffect(() => {
        if (dbCustomColor1) setCustomColor1(dbCustomColor1);
        if (dbCustomColor2) setCustomColor2(dbCustomColor2);
        if (dbCustomAccent) setCustomAccent(dbCustomAccent);
    }, [dbCustomColor1, dbCustomColor2, dbCustomAccent]);

    // Update form text when language or preset changes
    useEffect(() => {
        const activePreset = taglines[language][taglinePreset];
        setHeadlineText(activePreset.headline);
        setDescText(activePreset.desc);
    }, [language, taglinePreset]);

    const handleThemeChange = async (theme) => {
        setSelectedTheme(theme);
        if (!userId) return;

        try {
            setIsSaving(true);
            const adminRef = doc(db, 'franchiseAdmins', userId);
            await updateDoc(adminRef, {
                posterTheme: theme.name
            });
            
            Swal.fire({
                title: 'Theme Set!',
                text: `"${theme.name}" set as your default theme.`,
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2000
            });
        } catch (error) {
            console.error("Error saving theme preference:", error);
        } finally {
            setIsSaving(false);
        }
    };

    const handleCustomColorChange = async (type, val) => {
        let c1 = customColor1;
        let c2 = customColor2;
        let acc = customAccent;

        if (type === 'color1') {
            setCustomColor1(val);
            c1 = val;
        } else if (type === 'color2') {
            setCustomColor2(val);
            c2 = val;
        } else if (type === 'accent') {
            setCustomAccent(val);
            acc = val;
        }

        if (!userId) return;
        try {
            const adminRef = doc(db, 'franchiseAdmins', userId);
            await updateDoc(adminRef, {
                posterTheme: 'Custom Theme',
                customColor1: c1,
                customColor2: c2,
                customAccent: acc
            });
        } catch (error) {
            console.error("Error saving custom color:", error);
        }
    };

    // Dynamic styles based on theme selection
    const activeBg = templateStyle === 'light'
        ? 'linear-gradient(135deg, #ffffff 0%, #f1f5f9 100%)'
        : selectedTheme.name === 'Custom Theme' 
            ? `linear-gradient(135deg, ${customColor1} 0%, ${customColor2} 100%)` 
            : selectedTheme.bg;

    const activeAccent = templateStyle === 'light'
        ? '#1a7a5e' // Premium dark green accent for light mode
        : selectedTheme.name === 'Custom Theme' 
            ? customAccent 
            : selectedTheme.textAccent;

    const activeGlow = templateStyle === 'light'
        ? 'rgba(26, 122, 94, 0.05)'
        : selectedTheme.name === 'Custom Theme' 
            ? `${customAccent}33` 
            : selectedTheme.glow;

    const downloadPoster = async () => {
        if (!posterRef.current) return;
        try {
            Swal.fire({
                title: 'Generating Poster...',
                text: 'Rendering high resolution image, please wait.',
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const canvas = await html2canvas(posterRef.current, { 
                scale: 2, 
                useCORS: true,
                logging: false,
                backgroundColor: null
            });
            
            const image = canvas.toDataURL("image/png");
            
            Swal.close();

            const link = document.createElement('a');
            link.href = image;
            link.download = `Yathartha_Poster_${referralCode}.png`;
            link.click();

            Swal.fire({
                title: 'Success!',
                text: 'Poster downloaded successfully.',
                icon: 'success',
                confirmButtonColor: '#1a7a5e'
            });
        } catch (error) {
            console.error("Error downloading poster:", error);
            Swal.fire('Error', 'Failed to generate image. Please try again.', 'error');
        }
    };

    const copyReferralLink = () => {
        if (referralCode !== 'N/A') {
            navigator.clipboard.writeText(referralLink);
            Swal.fire({
                title: 'Copied!',
                text: 'Referral link copied to clipboard.',
                icon: 'success',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
        }
    };

    const handleWhatsAppShare = () => {
        const message = language === 'MR'
            ? `नमस्कार! तुमचे आर्थिक भविष्य सुरक्षित करण्यासाठी Yathartha चे मोफत वित्तीय निदान करा आणि मिळवा प्रिमियम रिपोर्ट.\n\nमाझा रेफरल कोड वापरा: *${referralCode}*\nथेट लिंकवर क्लिक करा: ${referralLink}`
            : `Hello! Secure your financial future today with Yathartha. Get a comprehensive financial diagnosis and build a roadmap to wealth.\n\nUse my Referral Code: *${referralCode}*\nClick here to start: ${referralLink}`;
        
        const encodedText = encodeURIComponent(message);
        window.open(`https://api.whatsapp.com/send?text=${encodedText}`, '_blank');
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
            {/* Share Link Section */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', display: 'flex', flexWrap: 'wrap', gap: '2rem', alignItems: 'center' }}>
                {referralCode !== 'N/A' && (
                    <div style={{ padding: '10px', background: 'white', borderRadius: '8px', border: '1px solid #e2e8f0', display: 'inline-block' }}>
                        <QRCodeSVG value={referralLink} size={100} />
                    </div>
                )}
                <div style={{ flex: 1, minWidth: '250px' }}>
                    <h3 style={{ margin: '0 0 0.5rem 0', color: '#1e293b' }}>Share Your Referral Link</h3>
                    <p style={{ margin: '0 0 1rem 0', color: '#64748b', fontSize: '0.9rem' }}>
                        Clients can scan the QR code or click this link. Your referral code will be automatically applied.
                    </p>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <input 
                            type="text" 
                            readOnly 
                            value={referralLink} 
                            style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', background: '#f8fafc', color: '#475569', fontSize: '0.9rem' }}
                        />
                        <button 
                            onClick={copyReferralLink}
                            style={{ background: '#1a7a5e', color: 'white', border: 'none', padding: '0 1.2rem', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px' }}
                        >
                            Copy Link
                        </button>
                    </div>
                </div>
            </div>

            {/* Poster Builder Customizer Layout */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 420px', gap: '2rem', alignItems: 'start', '@media (maxWidth: 968px)': { gridTemplateColumns: '1fr' } }}>
                
                {/* Control Panel Panel */}
                <div style={{ background: 'white', padding: '2rem', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                    <h3 style={{ margin: 0, color: '#1e293b', fontSize: '1.25rem', borderBottom: '2px solid #f1f5f9', paddingBottom: '1rem' }}> Live Poster Customizer</h3>
                    
                    {/* Size and Layout Template Style selector */}
                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: '150px' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>Design Theme Style</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={() => setTemplateStyle('dark')}
                                    style={{
                                        flex: 1,
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        border: templateStyle === 'dark' ? '2px solid #1a7a5e' : '1px solid #cbd5e1',
                                        background: templateStyle === 'dark' ? '#f0fdf4' : 'white',
                                        color: templateStyle === 'dark' ? '#166534' : '#475569',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    Sleek Dark
                                </button>
                                <button
                                    onClick={() => setTemplateStyle('light')}
                                    style={{
                                        flex: 1,
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        border: templateStyle === 'light' ? '2px solid #1a7a5e' : '1px solid #cbd5e1',
                                        background: templateStyle === 'light' ? '#f0fdf4' : 'white',
                                        color: templateStyle === 'light' ? '#166534' : '#475569',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    Elegant Light
                                </button>
                            </div>
                        </div>

                        {/* Language Selection */}
                        <div style={{ flex: 1, minWidth: '150px' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>Poster Language (भाषा)</label>
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={() => setLanguage('MR')}
                                    style={{
                                        flex: 1,
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        border: language === 'MR' ? '2px solid #1a7a5e' : '1px solid #cbd5e1',
                                        background: language === 'MR' ? '#f0fdf4' : 'white',
                                        color: language === 'MR' ? '#166534' : '#475569',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    मराठी (Marathi)
                                </button>
                                <button
                                    onClick={() => setLanguage('EN')}
                                    style={{
                                        flex: 1,
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        border: language === 'EN' ? '2px solid #1a7a5e' : '1px solid #cbd5e1',
                                        background: language === 'EN' ? '#f0fdf4' : 'white',
                                        color: language === 'EN' ? '#166534' : '#475569',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        fontSize: '0.85rem'
                                    }}
                                >
                                    English
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Tagline Selection Presets */}
                    <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '8px' }}>Select Marketing Catchphrase Preset</label>
                        <select
                            value={taglinePreset}
                            onChange={(e) => setTaglinePreset(Number(e.target.value))}
                            style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', color: '#1e293b', background: '#f8fafc', fontSize: '0.9rem', fontWeight: 500 }}
                        >
                            <option value={0}>Preset 1: Secure Financial Future (आर्थिक सुरक्षा)</option>
                            <option value={1}>Preset 2: Grow Family Wealth (गुंतवणूक आणि समृद्धी)</option>
                            <option value={2}>Preset 3: Retirement Planning (निवृत्ती नियोजन)</option>
                        </select>
                    </div>

                    {/* Text Editors */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                        <div>
                            <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '6px' }}>Headline Text</label>
                            <input
                                type="text"
                                value={headlineText}
                                onChange={(e) => setHeadlineText(e.target.value)}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box' }}
                            />
                        </div>
                        <div>
                            <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '6px' }}>Sub-description Text</label>
                            <textarea
                                value={descText}
                                onChange={(e) => setDescText(e.target.value)}
                                rows={2}
                                style={{ width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', fontSize: '0.9rem', boxSizing: 'border-box', fontFamily: 'inherit' }}
                            />
                        </div>
                    </div>

                    {/* Contact details checkboxes */}
                    <div>
                        <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '10px' }}>Contact Details to Show on Poster</label>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: '#334155' }}>
                                <input
                                    type="checkbox"
                                    checked={showPhone}
                                    onChange={(e) => setShowPhone(e.target.checked)}
                                    disabled={!userPhone}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#1a7a5e' }}
                                />
                                Show WhatsApp Number: <strong>{userPhone || 'N/A'}</strong>
                            </label>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.9rem', color: '#334155' }}>
                                <input
                                    type="checkbox"
                                    checked={showEmail}
                                    onChange={(e) => setShowEmail(e.target.checked)}
                                    disabled={!userEmail}
                                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#1a7a5e' }}
                                />
                                Show Email ID: <strong>{userEmail || 'N/A'}</strong>
                            </label>
                        </div>
                    </div>

                    {/* Theme selector (only for dark mode template) */}
                    {templateStyle === 'dark' && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <label style={{ fontSize: '0.85rem', fontWeight: 700, color: '#475569', display: 'block', marginBottom: '4px' }}>
                                Dark Theme Background Palette
                            </label>
                            
                            {/* Toggle between Presets and Custom Color Picker */}
                            <div style={{ display: 'flex', gap: '10px' }}>
                                <button
                                    onClick={() => {
                                        setSelectedTheme(themes[0]);
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        border: selectedTheme.name !== 'Custom Theme' ? '2px solid #1a7a5e' : '1px solid #cbd5e1',
                                        background: selectedTheme.name !== 'Custom Theme' ? '#f0fdf4' : 'white',
                                        color: selectedTheme.name !== 'Custom Theme' ? '#166534' : '#475569',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    Preset Themes
                                </button>
                                <button
                                    onClick={() => {
                                        setSelectedTheme(themes[5]); // Custom Theme
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '8px 12px',
                                        borderRadius: '8px',
                                        border: selectedTheme.name === 'Custom Theme' ? '2px solid #1a7a5e' : '1px solid #cbd5e1',
                                        background: selectedTheme.name === 'Custom Theme' ? '#f0fdf4' : 'white',
                                        color: selectedTheme.name === 'Custom Theme' ? '#166534' : '#475569',
                                        fontWeight: 600,
                                        cursor: 'pointer',
                                        fontSize: '0.8rem',
                                        transition: 'all 0.2s'
                                    }}
                                >
                                    🎨 Custom Theme Designer
                                </button>
                            </div>

                            {/* If Presets selected, show the circles */}
                            {selectedTheme.name !== 'Custom Theme' && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', background: '#f8fafc', padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                                    <div style={{ display: 'flex', gap: '10px' }}>
                                        {themes.slice(0, 5).map((theme, i) => (
                                            <button
                                                key={i}
                                                onClick={() => handleThemeChange(theme)}
                                                title={theme.name}
                                                style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '50%',
                                                    background: theme.bg,
                                                    border: selectedTheme.name === theme.name ? '3px solid #1a7a5e' : '1px solid #cbd5e1',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    transform: selectedTheme.name === theme.name ? 'scale(1.15)' : 'scale(1)',
                                                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                                                }}
                                            />
                                        ))}
                                    </div>
                                    <span style={{ fontSize: '0.8rem', color: '#1a7a5e', fontWeight: 700, marginLeft: 'auto' }}>
                                        {selectedTheme.name}
                                    </span>
                                </div>
                            )}

                            {/* If Custom Theme selected, show the gorgeous tabbed custom color picker! */}
                            {selectedTheme.name === 'Custom Theme' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', background: '#f8fafc', padding: '1.25rem', borderRadius: '12px', border: '1px solid #cbd5e1' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: '0.8rem', fontWeight: 700, color: '#1e293b' }}>🎨 Custom Color Pickers</span>
                                        <span style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 500 }}>(Changes apply instantly)</span>
                                    </div>
                                    
                                    {/* Color Selector Picker Tab buttons */}
                                    <div style={{ display: 'flex', gap: '8px', background: '#e2e8f0', padding: '4px', borderRadius: '8px' }}>
                                        <button
                                            onClick={() => setActivePickerTab('color1')}
                                            style={{
                                                flex: 1,
                                                padding: '8px 10px',
                                                borderRadius: '6px',
                                                border: 'none',
                                                background: activePickerTab === 'color1' ? '#1e293b' : 'transparent',
                                                color: activePickerTab === 'color1' ? 'white' : '#475569',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                fontSize: '0.75rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: customColor1, border: '1px solid white' }}></div>
                                            Start
                                        </button>
                                        <button
                                            onClick={() => setActivePickerTab('color2')}
                                            style={{
                                                flex: 1,
                                                padding: '8px 10px',
                                                borderRadius: '6px',
                                                border: 'none',
                                                background: activePickerTab === 'color2' ? '#1e293b' : 'transparent',
                                                color: activePickerTab === 'color2' ? 'white' : '#475569',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                fontSize: '0.75rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: customColor2, border: '1px solid white' }}></div>
                                            End
                                        </button>
                                        <button
                                            onClick={() => setActivePickerTab('accent')}
                                            style={{
                                                flex: 1,
                                                padding: '8px 10px',
                                                borderRadius: '6px',
                                                border: 'none',
                                                background: activePickerTab === 'accent' ? '#1e293b' : 'transparent',
                                                color: activePickerTab === 'accent' ? 'white' : '#475569',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                fontSize: '0.75rem',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <div style={{ width: '12px', height: '12px', borderRadius: '50%', backgroundColor: customAccent, border: '1px solid white' }}></div>
                                            Accent
                                        </button>
                                    </div>

                                    {/* Custom Embedded Color Picker widget display */}
                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '4px' }}>
                                        {activePickerTab === 'color1' && (
                                            <ColorPickerWidget 
                                                activeColor={customColor1} 
                                                onColorChange={(val) => handleCustomColorChange('color1', val)} 
                                                label="Gradient Start Color"
                                            />
                                        )}
                                        {activePickerTab === 'color2' && (
                                            <ColorPickerWidget 
                                                activeColor={customColor2} 
                                                onColorChange={(val) => handleCustomColorChange('color2', val)} 
                                                label="Gradient End Color"
                                            />
                                        )}
                                        {activePickerTab === 'accent' && (
                                            <ColorPickerWidget 
                                                activeColor={customAccent} 
                                                onColorChange={(val) => handleCustomColorChange('accent', val)} 
                                                label="Brand Accent Color"
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Action buttons sharing */}
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                        <button 
                            onClick={downloadPoster}
                            style={{ 
                                flex: 1, 
                                minWidth: '150px',
                                background: '#1a7a5e', 
                                color: 'white', 
                                border: 'none', 
                                padding: '12px 18px', 
                                borderRadius: '8px', 
                                cursor: 'pointer', 
                                fontWeight: 700, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                gap: '8px',
                                boxShadow: '0 4px 10px rgba(26,122,94,0.2)'
                            }}
                        >
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                            Download PNG
                        </button>
                        <button 
                            onClick={handleWhatsAppShare}
                            style={{ 
                                flex: 1, 
                                minWidth: '150px',
                                background: '#25D366', 
                                color: 'white', 
                                border: 'none', 
                                padding: '12px 18px', 
                                borderRadius: '8px', 
                                cursor: 'pointer', 
                                fontWeight: 700, 
                                display: 'flex', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                gap: '8px',
                                boxShadow: '0 4px 10px rgba(37,211,102,0.2)'
                            }}
                        >
                            <svg width="18" height="18" fill="currentColor" viewBox="0 0 24 24"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946C.06 5.348 5.397.01 12.008.01c3.202.001 6.212 1.246 8.477 3.513 2.262 2.268 3.507 5.28 3.505 8.484-.004 6.657-5.34 11.997-11.953 11.997-2.005-.001-3.973-.502-5.724-1.455L0 24zm6.59-4.846c1.6.95 2.766 1.488 4.773 1.489 5.485 0 9.95-4.467 9.953-9.953.001-2.657-1.02-5.155-2.876-7.01C16.59 1.83 14.09 .81 11.432.81c-5.484 0-9.95 4.467-9.954 9.953-.001 1.95.507 3.328 1.442 4.95l-.996 3.633 3.725-.977zm11.367-7.94c-.266-.134-1.574-.775-1.817-.864-.243-.087-.419-.133-.596.133-.177.266-.685.864-.84 1.042-.155.178-.31.198-.576.066-.266-.134-1.123-.414-2.14-1.32-.79-1.054-1.326-2.355-1.48-2.62-.156-.268-.017-.413.116-.546.12-.12.266-.31.398-.466.134-.155.178-.266.266-.444.089-.178.044-.333-.022-.466-.067-.133-.596-1.436-.816-1.97-.215-.518-.432-.448-.596-.456-.153-.007-.33-.009-.507-.009-.177 0-.465.066-.708.33-.243.266-.93.91-.93 2.22 0 1.31.954 2.576 1.087 2.754.133.178 1.878 2.87 4.548 4.02.635.273 1.13.437 1.517.56.638.204 1.218.175 1.678.106.512-.077 1.574-.643 1.796-1.264.22-.62.22-1.15.155-1.263-.066-.112-.243-.198-.51-.33z"/></svg>
                            WhatsApp Share
                        </button>
                    </div>
                </div>

                {/* Poster Preview Window */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                    <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Live Preview</div>
                    
                    {/* The Poster to be Captured */}
                    <div 
                        ref={posterRef}
                        style={{ 
                            width: '400px', 
                            height: '600px', 
                            background: activeBg, 
                            borderRadius: '16px', 
                            position: 'relative',
                            overflow: 'hidden',
                            fontFamily: "'Inter', sans-serif",
                            color: templateStyle === 'light' ? '#0f172a' : 'white',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                            transition: 'all 0.3s ease',
                            border: templateStyle === 'light' ? '1px solid rgba(0,0,0,0.08)' : 'none'
                        }}
                    >
                        {/* Decorative Elements */}
                        <div style={{ position: 'absolute', top: '-50px', right: '-50px', width: '200px', height: '200px', background: activeGlow, borderRadius: '50%', filter: 'blur(30px)' }}></div>
                        <div style={{ position: 'absolute', bottom: '-50px', left: '-50px', width: '250px', height: '250px', background: activeGlow, borderRadius: '50%', filter: 'blur(40px)' }}></div>
                        
                        <div style={{ padding: '2.25rem', display: 'flex', flexDirection: 'column', height: '100%', boxSizing: 'border-box', position: 'relative', zIndex: 2 }}>
                            {/* Logo */}
                            <div style={{ 
                                fontSize: '1.4rem', 
                                fontWeight: 800, 
                                color: activeAccent, 
                                marginBottom: '1.25rem', 
                                letterSpacing: '1.5px', 
                                transition: 'color 0.3s ease' 
                            }}>
                                Yathartha
                            </div>
                            
                            {/* Headline */}
                            <h2 style={{ 
                                fontSize: '2.1rem', 
                                margin: '0 0 1rem 0', 
                                lineHeight: 1.25,
                                fontWeight: 800,
                                color: templateStyle === 'light' ? '#0f172a' : 'white'
                            }}>
                                {headlineText}
                            </h2>
                            
                            {/* Description */}
                            <p style={{ 
                                color: templateStyle === 'light' ? '#475569' : '#94a3b8', 
                                fontSize: '1rem', 
                                margin: '0 0 2rem 0', 
                                lineHeight: 1.5,
                                fontWeight: 500
                            }}>
                                {descText}
                            </p>
                            
                            {/* Info Card Block */}
                            <div style={{ 
                                marginTop: 'auto', 
                                background: templateStyle === 'light' ? 'rgba(0,0,0,0.02)' : 'rgba(255,255,255,0.04)', 
                                padding: '1.25rem', 
                                borderRadius: '12px', 
                                backdropFilter: 'blur(10px)', 
                                border: templateStyle === 'light' ? '1px solid rgba(0,0,0,0.08)' : '1px solid rgba(255,255,255,0.08)', 
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: '1.25rem' 
                            }}>
                                <div style={{ background: 'white', padding: '8px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <QRCodeSVG value={referralLink} size={85} />
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ fontSize: '0.75rem', color: templateStyle === 'light' ? '#64748b' : '#94a3b8', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '3px', fontWeight: 700 }}>
                                        {language === 'MR' ? 'स्कॅन करून सुरु करा' : 'Scan to Start'}
                                    </div>
                                    <div style={{ fontWeight: 600, fontSize: '0.85rem', color: templateStyle === 'light' ? '#475569' : '#cbd5e1' }}>
                                        {language === 'MR' ? 'अधिकृत भागीदार:' : 'Authorized Partner:'}
                                    </div>
                                    <div style={{ 
                                        color: activeAccent, 
                                        fontSize: '1rem', 
                                        fontWeight: 800, 
                                        transition: 'color 0.3s ease',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {franchiseName}
                                    </div>
                                    
                                    {/* Optional WhatsApp display */}
                                    {showPhone && userPhone && (
                                        <div style={{ fontSize: '0.8rem', color: templateStyle === 'light' ? '#334155' : '#e2e8f0', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}>
                                            <span style={{ color: '#25D366' }}>WA:</span> {userPhone}
                                        </div>
                                    )}

                                    {/* Optional Email display */}
                                    {showEmail && userEmail && (
                                        <div style={{ 
                                            fontSize: '0.75rem', 
                                            color: templateStyle === 'light' ? '#64748b' : '#cbd5e1', 
                                            marginTop: '2px', 
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            fontWeight: 500
                                        }}>
                                            {userEmail}
                                        </div>
                                    )}

                                    <div style={{ fontSize: '0.75rem', color: templateStyle === 'light' ? '#64748b' : '#cbd5e1', marginTop: '5px', fontWeight: 600 }}>
                                        {language === 'MR' ? 'कोड:' : 'Code:'} {referralCode}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MarketingKit;
